<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import AgentLayout from "./layouts/AgentLayout.vue";
import PageHeader from "@/components/shared/PageHeader.vue";
import bulkDeleteMedia from "@/components/mediaLibrary/bulkDeleteMedia.vue";
import { useCompanyStore } from "@/stores/company";
import { useUiStore } from "@/stores/ui";
import { useMediaStore } from "@/stores/media";
const router = useRouter();
const uiStore = useUiStore();
const mediaStore = useMediaStore();
const mediaArr = ref([]);
const searchQuery = ref("");
const sortOption = ref("Date Added (Newest)");
const selectedFileTypes = ref(["image", "video", "document"]);
const selectedDateFilter = ref("any"); // 'any', '7days', '30days'
const companyId = useCompanyStore().companyId;
let getModalState = computed(() => uiStore.modalState);
const isLoading = ref(false);

const navigateToCreateMedia = () => {
  router.push("/media-library/create");
};

const refreshGrid = () => {
  document.querySelector(".modal-backdrop").remove();
  document.querySelector(".dx-device-desktop").classList.remove("modal-open");
  fetchAllMediaGrid();
};

const fetchAllMediaGrid = () => {
  isLoading.value = true;
  mediaStore
    .fetchAllMediaGrid(companyId)
    .then((res) => {
      mediaArr.value = res.data.map((item) => ({
        ...item,
        createdAt: new Date(item.createdAt),
        selected: false,
      }));
      isLoading.value = false;
    })
    .catch(() => {
    });
};

const isGridView = ref(true);

const toggleView = (mode) => {
  isGridView.value = mode === "grid";
};

const parseSize = (sizeStr) => {
  if (!sizeStr || sizeStr === "N/A") return 0;
  const units = { KB: 1, MB: 1024, GB: 1024 * 1024 };
  const parts = sizeStr.split(" ");
  if (parts.length !== 2) return 0;
  const value = parseFloat(parts[0]);
  const unit = parts[1];
  return value * (units[unit] || 0);
};

const selectedOrientation = ref("any"); // 'any', 'landscape', 'portrait', 'square'

const getOrientation = (dimensions) => {
  if (!dimensions || typeof dimensions !== "string" || dimensions === "1080p")
    return "landscape";
  const parts = dimensions.split("x");
  if (parts.length !== 2) return "landscape";
  const width = parseInt(parts[0]);
  const height = parseInt(parts[1]);
  if (isNaN(width) || isNaN(height)) return "landscape";
  if (width > height) return "landscape";
  if (height > width) return "portrait";
  return "square";
};

const filteredMedia = computed(() => {
  let filtered = mediaArr.value.filter((item) => {
    // Search Text
    const matchesSearch = item.name
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase());

    // File Type
    const matchesType = selectedFileTypes.value.includes(item.type);

    // Date
    let matchesDate = true;
    if (selectedDateFilter.value !== "any") {
      const now = new Date();
      const diffTime = Math.abs(now - item.createdAt);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (selectedDateFilter.value === "7days") {
        matchesDate = diffDays <= 7;
      } else if (selectedDateFilter.value === "30days") {
        matchesDate = diffDays <= 30;
      }
    }

    // Orientation
    let matchesOrientation = true;
    if (selectedOrientation.value !== "any") {
      matchesOrientation =
        getOrientation(item.dimensions) === selectedOrientation.value;
    }

    return matchesSearch && matchesType && matchesDate && matchesOrientation;
  });

  // Sort
  filtered.sort((a, b) => {
    if (sortOption.value === "Name (A-Z)") {
      return a.name.localeCompare(b.name);
    } else if (sortOption.value === "Size (Largest)") {
      return parseSize(b.size) - parseSize(a.size);
    } else {
      // Date Added (Newest) - default
      return b.createdAt - a.createdAt;
    }
  });

  return filtered;
});

const groupedMedia = computed(() => {
  const groups = {};
  filteredMedia.value.forEach((item) => {
    const groupName = item.dateGroup || "Unknown Date";
    if (!groups[groupName]) {
      groups[groupName] = [];
    }
    groups[groupName].push(item);
  });
  return groups;
});

const mediaCounts = computed(() => {
  return {
    image: mediaArr.value.filter((m) => m.type === "image").length,
    video: mediaArr.value.filter((m) => m.type === "video").length,
    document: mediaArr.value.filter((m) => m.type === "document").length,
  };
});

const toggleSelection = (id) => {
  const item = mediaArr.value.find((i) => i.id === id);
  if (item) {
    item.selected = !item.selected;
  }
};

const selectedCount = computed(() => {
  return mediaArr.value.filter((i) => i.selected).length;
});

const typeIcon = (type) =>
  ({
    image: "bi bi-image",
    video: "bi bi-camera-video-fill",
    document: "bi bi-file-earmark-text-fill",
  })[type] || "bi bi-file-earmark";

const action = async (action, data) => {
  uiStore.setModalAction(action);
  uiStore.setModalState(true);
  uiStore.setModalData(data);
};

const deleteSelected = async () => {
  const selectedIds = await mediaArr.value
    .filter((i) => i.selected)
    .map((i) => i.id);
  await action("bulk_delete_media", { ids: selectedIds });
};

const isFilterLoading = ref(false);

watch(
  [
    searchQuery,
    selectedFileTypes,
    selectedDateFilter,
    selectedOrientation,
    sortOption,
  ],
  () => {
    isFilterLoading.value = true;
    setTimeout(() => {
      isFilterLoading.value = false;
    }, 500);
  }
);

onMounted(() => {
  fetchAllMediaGrid();
});
</script>

<template>
  <bulkDeleteMedia v-if="getModalState" @refresh="refreshGrid" />
  <AgentLayout>
    <div class="media-library-container">
      <PageHeader
        title="Media Library"
        subtitle="Manage, organize, and select your creative assets."
        :showButton="true"
        buttonText="Upload New Media"
        @action-click="navigateToCreateMedia"
      >
        <template #icon>
          <i class="bi bi-cloud-arrow-up-fill"></i>
        </template>
      </PageHeader>

      <div class="white-card">
        <div class="toolbar">
          <div class="search-wrapper">
            <span class="search-icon bi bi-search"></span>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by name or date..."
              class="search-input"
            />
          </div>
          <div class="mass-actions" v-if="selectedCount > 0">
            <!-- <div class="vertical-divider"></div> -->
            <button
              class="btn-danger-icon"
              @click="deleteSelected"
              title="Delete Selected"
            >
              <i class="bi bi-trash3-fill"></i>
              <span class="btn-text">Delete ({{ selectedCount }})</span>
            </button>
          </div>
          <div class="actions-wrapper">
            <div class="sort-wrapper">
              <span class="label">Sort by:</span>
              <select v-model="sortOption" class="sort-select">
                <option>Date Added (Newest)</option>
                <option>Name (A-Z)</option>
                <option>Size (Largest)</option>
              </select>
            </div>
            <div class="view-toggle">
              <button
                class="toggle-btn"
                :class="{ active: isGridView }"
                @click="toggleView('grid')"
              >
                <i class="bi bi-grid-fill"></i>
              </button>
              <button
                class="toggle-btn"
                :class="{ active: !isGridView }"
                @click="toggleView('list')"
              >
                <i class="bi bi-list-ul"></i>
              </button>
            </div>
          </div>
        </div>

        <div class="content-area">
          <div class="filter-sidebar">
            <!-- Filter groups omitted for brevity, keeping existing structure -->
            <div class="filter-group">
              <h3 class="filter-title">FILE TYPE</h3>
              <label class="checkbox-item">
                <input
                  type="checkbox"
                  value="image"
                  v-model="selectedFileTypes"
                />
                <i class="bi bi-image filter-icon"></i>
                Images
                <span class="count">{{ mediaCounts.image }}</span>
              </label>
              <label class="checkbox-item">
                <input
                  type="checkbox"
                  value="video"
                  v-model="selectedFileTypes"
                />
                <i class="bi bi-camera-video-fill filter-icon"></i>
                Videos <span class="count">{{ mediaCounts.video }}</span>
              </label>
              <!-- <label class="checkbox-item">
                <input
                  type="checkbox"
                  value="document"
                  v-model="selectedFileTypes"
                />
                Documents <span class="count">8</span>
              </label> -->
            </div>

            <div class="filter-group">
              <h3 class="filter-title">DATE</h3>
              <label class="radio-item">
                <input
                  type="radio"
                  name="date"
                  value="any"
                  v-model="selectedDateFilter"
                />
                Any time
              </label>
              <label class="radio-item">
                <input
                  type="radio"
                  name="date"
                  value="7days"
                  v-model="selectedDateFilter"
                />
                Last 7 days
              </label>
              <label class="radio-item">
                <input
                  type="radio"
                  name="date"
                  value="30days"
                  v-model="selectedDateFilter"
                />
                Last 30 days
              </label>
            </div>
            <!-- 
            <div class="filter-group">
              <h3 class="filter-title">ORIENTATION</h3>
              <div class="orientation-options">
                <button
                  class="orientation-btn landscape"
                  :class="{ active: selectedOrientation === 'landscape' }"
                  @click="
                    selectedOrientation =
                      selectedOrientation === 'landscape' ? 'any' : 'landscape'
                  "
                >
                  ▭
                </button>
                <button
                  class="orientation-btn portrait"
                  :class="{ active: selectedOrientation === 'portrait' }"
                  @click="
                    selectedOrientation =
                      selectedOrientation === 'portrait' ? 'any' : 'portrait'
                  "
                >
                  ▯
                </button>
                <button
                  class="orientation-btn square"
                  :class="{ active: selectedOrientation === 'square' }"
                  @click="
                    selectedOrientation =
                      selectedOrientation === 'square' ? 'any' : 'square'
                  "
                >
                  ◻
                </button>
              </div>
            </div> -->
          </div>

          <div class="media-grid-wrapper">
            <div v-if="isLoading || isFilterLoading" class="media-grid skeleton-grid">
              <div v-for="n in 10" :key="n" class="skeleton-card">
                <div class="skeleton-thumb"></div>
                <div class="skeleton-line"></div>
                <div class="skeleton-line short"></div>
              </div>
            </div>
            <template v-else>
              <template
                v-for="(items, groupName) in groupedMedia"
                :key="groupName"
              >
                <div class="group-header">
                  <h2 class="group-title">
                    {{ groupName }}
                    <span class="badge">{{ items.length }} items</span>
                  </h2>
                </div>
                <!-- GRID VIEW -->
                <div v-if="isGridView" class="media-grid">
                  <div
                    v-for="(item, idx) in items"
                    :key="item.id"
                    class="media-card"
                    :class="{ selected: item.selected }"
                    :style="{ '--d': idx }"
                    @click="toggleSelection(item.id)"
                  >
                    <div class="card-preview">
                      <div class="selection-indicator">
                        <i v-if="item.selected" class="bi bi-check-lg"></i>
                      </div>
                      <span class="type-chip">
                        <i :class="typeIcon(item.type)"></i>
                      </span>
                      <img
                        v-if="item.type !== 'video'"
                        :src="item.url"
                        :alt="item.name"
                        class="media-img"
                      />
                      <video
                        v-else
                        :src="item.url"
                        class="media-img"
                        preload="metadata"
                        muted
                        loop
                        onmouseover="this.play()"
                        onmouseout="this.pause()"
                      ></video>
                      <div v-if="item.type === 'video'" class="play-overlay">
                        <i class="bi bi-play-fill"></i>
                      </div>
                      <div v-if="item.type === 'video'" class="duration-badge">
                        {{ item.duration }}
                      </div>
                      <div class="preview-overlay"></div>
                    </div>
                    <div class="card-details">
                      <h3 class="media-name" :title="item.name">
                        {{ item.name }}
                      </h3>
                      <div class="media-meta">
                        <span v-if="item.source !== 'url'">{{
                          item.size
                        }}</span>
                        <span>{{ item.dimensions }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- LIST VIEW -->
                <div v-else class="media-list">
                  <div
                    v-for="item in items"
                    :key="item.id"
                    class="media-list-item"
                    :class="{ selected: item.selected }"
                    @click="toggleSelection(item.id)"
                  >
                    <div class="list-preview">
                      <img
                        v-if="item.type !== 'video'"
                        :src="item.url"
                        :alt="item.name"
                        class="list-img"
                      />
                      <video
                        v-else
                        :src="item.url"
                        class="list-img"
                        preload="metadata"
                        muted
                        playsinline
                      ></video>
                      <div v-if="item.type === 'video'" class="list-duration">
                        {{ item.duration }}
                      </div>
                    </div>
                    <div class="list-info">
                      <h3 class="list-name">{{ item.name }}</h3>
                      <div class="list-meta">
                        <div class="meta-row">
                          <span class="meta-tag">{{ item.type }}</span>
                          <span v-if="item.dimensions" class="meta-separator"
                            >•</span
                          >
                          <span v-if="item.dimensions">{{
                            item.dimensions
                          }}</span>
                        </div>
                        <div v-if="item.source !== 'url'" class="list-size">
                          {{ item.size }}
                        </div>
                      </div>
                    </div>
                    <div class="list-selection">
                      <div
                        class="selection-checkbox"
                        :class="{ checked: item.selected }"
                      >
                        <i v-if="item.selected" class="bi bi-check-lg"></i>
                      </div>
                      <!-- <div
                        class="delete-btn-list"
                        @click="(e) => deleteMedia(item.id, e)"
                      >
                        <i class="bi bi-trash3-fill"></i>
                      </div> -->
                    </div>
                  </div>
                </div>
              </template>

              <div
                v-if="
                  filteredMedia.length === 0 && !isLoading && !isFilterLoading
                "
                class="empty-state"
              >
                <div class="empty-icon">📁</div>
                <h3 class="empty-title">No assets found</h3>
                <p class="empty-subtitle">
                  Try adjusting your search or filters, or upload a new asset.
                </p>
                <button class="btn btn-primary" @click="navigateToCreateMedia">
                  Upload New Media
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </AgentLayout>
</template>

<style scoped>
.media-library-container {
  color: var(--text-primary);
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.white-card {
  background: var(--neutral-0);
  border-radius: 16px;
  padding: 32px;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Header styles removed, now in PageHeader component */

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: var(--bg-secondary);
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 32px;
  border: 1px solid var(--neutral-200);
}

.mass-actions {
  display: flex;
  align-items: center;
  margin-right: 12px;
  animation: fadeIn 0.3s ease;
}

.btn-danger-icon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: #ef4444;
  color: #ffffff;
  border: 1px solid #ef4444;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(239, 68, 68, 0.25);
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.05s ease;
}

.btn-danger-icon i {
  font-size: 14px;
  line-height: 1;
}

.btn-danger-icon .btn-text {
  padding: 0 !important;
  font-size: 14px;
  font-weight: 600;
  color: inherit !important;
  text-decoration: none !important;
}

.btn-danger-icon:hover {
  background-color: #dc2626;
  border-color: #dc2626;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(239, 68, 68, 0.35);
}

.btn-danger-icon:active {
  transform: translateY(1px);
  box-shadow: 0 1px 2px rgba(239, 68, 68, 0.2);
}

.btn-danger-icon:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.3);
}

.vertical-divider {
  width: 1px;
  height: 24px;
  background-color: var(--neutral-200);
  margin: 0 12px;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.search-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid var(--neutral-300);
  border-radius: 8px;
  padding: 8px 12px;
  flex: 1;
  min-width: 0;
  max-width: 540px;
  margin-right: 16px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.search-wrapper:focus-within {
  border-color: var(--primary-color, #4e7fff);
  box-shadow: 0 0 0 3px rgba(78, 127, 255, 0.12);
}

.search-icon {
  margin-right: 8px;
  color: var(--neutral-400);
}

.search-input {
  border: none;
  outline: none;
  width: 100%;
  font-size: 14px;
  color: var(--neutral-700);
  background: transparent;
}

.search-input::placeholder {
  color: var(--neutral-400);
  opacity: 1;
}

/* Kill autofill yellow bg */
.search-input:-webkit-autofill,
.search-input:-webkit-autofill:hover,
.search-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px white inset;
  -webkit-text-fill-color: var(--neutral-700);
  caret-color: var(--neutral-700);
  transition: background-color 5000s ease-in-out 0s;
}

.actions-wrapper {
  display: flex;
  align-items: center;
  gap: 24px;
}

.sort-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--text-secondary);
}

.sort-select {
  border: 1px solid var(--neutral-300);
  border-radius: 4px;
  padding: 6px;
  background: white;
  color: var(--neutral-700);
  outline: none;
}

.view-toggle {
  display: flex;
  background: var(--neutral-200);
  border-radius: 6px;
  padding: 2px;
}

.toggle-btn {
  border: none;
  background: transparent;
  padding: 6px 10px;
  cursor: pointer;
  border-radius: 4px;
  color: var(--text-secondary);
}

.toggle-btn.active {
  background: white;
  color: var(--primary-color);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.content-area {
  display: flex;
  gap: 32px;
  flex: 1;
  overflow: hidden;
}

.filter-sidebar {
  width: 240px;
  flex-shrink: 0;
  overflow-y: auto;
}

.filter-group {
  margin-bottom: 32px;
}

.filter-title {
  font-size: 12px;
  text-transform: uppercase;
  color: var(--neutral-400);
  font-weight: 700;
  margin-bottom: 16px;
  letter-spacing: 0.05em;
}

.checkbox-item,
.radio-item {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-size: 14px;
  color: var(--neutral-600);
  cursor: pointer;
}

.checkbox-item input,
.radio-item input {
  margin-right: 10px;
  accent-color: var(--primary-color);
}

.count {
  margin-left: auto;
  color: var(--neutral-400);
  font-size: 12px;
}

.orientation-options {
  display: flex;
  gap: 8px;
}

.orientation-btn {
  border: 1px solid var(--neutral-300);
  background: white;
  border-radius: 4px;
  width: 40px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
}

.orientation-btn:hover {
  background: var(--neutral-100);
}

.orientation-btn.active {
  background: #eff6ff;
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.media-grid-wrapper {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px; /* Space for scrollbar */
}

.group-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  margin-top: 8px;
}

.group-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--neutral-900);
}

.badge {
  background: var(--neutral-100);
  color: var(--text-secondary);
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 12px;
  font-weight: 500;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.media-card {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--neutral-200);
  transition: transform 0.2s, box-shadow 0.2s;
  cursor: pointer;
}

.media-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
}

.media-card.selected {
  border: 2px solid var(--primary-color);
}

.card-preview {
  position: relative;
  aspect-ratio: 1;
  background: var(--bg-secondary);
}

.media-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.selection-indicator {
  position: absolute;
  top: 10px;
  left: 10px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: white;
  border: 1px solid var(--neutral-300);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  z-index: 2;
}

.media-card.selected .selection-indicator {
  background: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
  font-size: 12px;
}

.duration-badge {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
}

.card-details {
  padding: 12px;
}

.media-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--neutral-700);
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.media-meta {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--neutral-400);
}

.media-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
}

.media-list-item {
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    box-shadow 0.15s ease, transform 0.15s ease;
}

.media-list-item:hover {
  background-color: var(--bg-secondary);
  border-color: var(--neutral-300);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.media-list-item.selected {
  border-color: var(--primary-color);
  background-color: #eff6ff;
  box-shadow: 0 0 0 1px rgba(47, 77, 132, 0.25);
}

.list-preview {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  margin-right: 16px;
  background: var(--neutral-100);
  flex-shrink: 0;
}

.list-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.list-duration {
  position: absolute;
  bottom: 4px;
  right: 4px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 2px;
}

.list-info {
  flex: 1;
  min-width: 0;
}

.list-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--neutral-700);
  margin: 0 0 4px 0;
  word-break: break-word;
}

.list-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: var(--neutral-400);
}

.meta-row {
  display: flex;
  align-items: center;
}

.meta-tag {
  text-transform: capitalize;
}

.meta-separator {
  margin: 0 6px;
}

.list-size {
  /* width: 80px; Remove fixed width */
  text-align: left; /* Align left */
  font-size: 12px;
  color: var(--text-secondary);
  /* margin-right: 24px; Remove margin */
}

.list-selection {
  width: 24px;
}

.selection-checkbox {
  width: 20px;
  height: 20px;
  border: 1px solid var(--neutral-300);
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
}

.selection-checkbox.checked {
  background: var(--primary-color);
  border-color: var(--primary-color);
}

.delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  background: white;
  border-radius: 50%;
  border: 1px solid var(--neutral-300);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  cursor: pointer;
  z-index: 2;
  opacity: 1;
  transition: opacity 0.2s;
}

.delete-btn:hover {
  background: #fee2e2;
  border-color: #ef4444;
}

.delete-btn-list {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  margin-left: 8px;
  border-radius: 4px;
  color: var(--text-secondary);
  opacity: 1;
  transition: opacity 0.2s;
}

.delete-btn-list:hover {
  background: #fee2e2;
  color: #ef4444;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
  min-height: 400px;
  width: 100%;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.5;
}

.empty-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.empty-subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin-bottom: 32px;
  max-width: 400px;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-primary:hover {
  background-color: var(--primary-hover);
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* ── Dark mode ─────────────────────────────────────────────── */
[data-theme="dark"] .media-library-container {
  color: #e8edf5;
}

[data-theme="dark"] .white-card {
  background: #1e2a3e;
  border-color: #2d3f5a;
}

[data-theme="dark"] .toolbar {
  background-color: #161c2c;
  border-color: #2d3f5a;
}

[data-theme="dark"] .search-wrapper {
  background: #28364e;
  border-color: #3d5070;
}

[data-theme="dark"] .search-wrapper:focus-within {
  border-color: #4e7fff;
  box-shadow: 0 0 0 3px rgba(78, 127, 255, 0.18);
}

[data-theme="dark"] .search-icon {
  color: #6480a0;
}

[data-theme="dark"] .search-input {
  color: #e8edf5;
}

[data-theme="dark"] .search-input::placeholder {
  color: #6480a0;
}

[data-theme="dark"] .sort-wrapper {
  color: #9aadc4;
}

[data-theme="dark"] .sort-select {
  background: #28364e;
  color: #e8edf5;
  border-color: #3d5070;
}

[data-theme="dark"] .view-toggle {
  background: #161c2c;
}

[data-theme="dark"] .toggle-btn {
  color: #9aadc4;
}

[data-theme="dark"] .toggle-btn.active {
  background: #3d5070;
  color: #4e7fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}

[data-theme="dark"] .filter-title {
  color: #6480a0;
}

[data-theme="dark"] .checkbox-item,
[data-theme="dark"] .radio-item {
  color: #9aadc4;
}

[data-theme="dark"] .checkbox-item input,
[data-theme="dark"] .radio-item input {
  accent-color: #4e7fff;
}

[data-theme="dark"] .count {
  color: #6480a0;
}

[data-theme="dark"] .group-title {
  color: #e8edf5;
}

[data-theme="dark"] .badge {
  background: #28364e;
  color: #9aadc4;
}

[data-theme="dark"] .media-card {
  background: #28364e;
  border-color: #3d5070;
}

[data-theme="dark"] .media-card:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .card-preview {
  background: #1e2a3e;
}

[data-theme="dark"] .media-name {
  color: #e8edf5;
}

[data-theme="dark"] .media-meta {
  color: #6480a0;
}

[data-theme="dark"] .selection-indicator {
  background: #28364e;
  border-color: #3d5070;
}

[data-theme="dark"] .media-list-item {
  background: #28364e;
  border-color: #3d5070;
}

[data-theme="dark"] .media-list-item:hover {
  background-color: #324663;
  border-color: #4e6488;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}

[data-theme="dark"] .media-list-item.selected {
  border-color: #4e7fff;
  background-color: #1a2d52;
  box-shadow: 0 0 0 1px rgba(78, 127, 255, 0.4);
}

[data-theme="dark"] .list-preview {
  background: #1e2a3e;
}

[data-theme="dark"] .list-name {
  color: #e8edf5;
}

[data-theme="dark"] .list-meta {
  color: #6480a0;
}

[data-theme="dark"] .list-size {
  color: #9aadc4;
}

[data-theme="dark"] .selection-checkbox {
  background: #28364e;
  border-color: #3d5070;
}

[data-theme="dark"] .vertical-divider {
  background-color: #2d3f5a;
}

[data-theme="dark"] .btn-danger-icon {
  background-color: #ef4444;
  color: #ffffff;
  border-color: #ef4444;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
}

[data-theme="dark"] .btn-danger-icon:hover {
  background-color: #f87171;
  border-color: #f87171;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.55);
}

[data-theme="dark"] .btn-danger-icon:focus-visible {
  box-shadow: 0 0 0 3px rgba(248, 113, 113, 0.4);
}

[data-theme="dark"] .btn-danger-icon .btn-text {
  color: inherit !important;
  text-decoration: none !important;
}

[data-theme="dark"] .search-input::placeholder {
  color: #6480a0;
}

[data-theme="dark"] .search-input:-webkit-autofill,
[data-theme="dark"] .search-input:-webkit-autofill:hover,
[data-theme="dark"] .search-input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0 1000px #28364e inset;
  -webkit-text-fill-color: #e8edf5;
  caret-color: #e8edf5;
}

[data-theme="dark"] .empty-title {
  color: #e8edf5;
}

[data-theme="dark"] .empty-subtitle {
  color: #9aadc4;
}

/* ============================================================
   ── Enhancement layer — refined asset gallery ──────────────
   Layered after the base rules; wins by source order. Adds
   zoomable previews, type/play affordances, brand selection,
   count pills, staggered reveal, and a shimmer skeleton.
   ============================================================ */

/* Card polish */
.media-card { border-radius: 14px; }
.media-card:hover { box-shadow: 0 14px 28px -12px rgba(15, 23, 42, 0.25); }

/* Preview: clip + zoomable media + reveal overlay */
.card-preview { overflow: hidden; }
.media-img { transition: transform 0.45s cubic-bezier(0.2, 0.7, 0.2, 1); }
.media-card:hover .media-img { transform: scale(1.06); }

.preview-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to top, rgba(15, 23, 42, 0.3), transparent 45%);
  opacity: 0;
  transition: opacity 0.25s ease;
  pointer-events: none;
}
.media-card:hover .preview-overlay { opacity: 1; }

/* Media-type chip */
.type-chip {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
  width: 26px;
  height: 26px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  font-size: 12px;
  backdrop-filter: blur(4px);
  opacity: 0;
  transform: translateY(-4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.media-card:hover .type-chip,
.media-card.selected .type-chip { opacity: 1; transform: translateY(0); }

/* Video play affordance */
.play-overlay {
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
  z-index: 1;
  pointer-events: none;
}
.play-overlay i {
  width: 46px;
  height: 46px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: rgba(15, 23, 42, 0.55);
  color: #fff;
  font-size: 22px;
  backdrop-filter: blur(4px);
  transition: transform 0.2s ease, background 0.2s ease;
}
.media-card:hover .play-overlay i {
  transform: scale(1.08);
  background: var(--primary-color, #2f4476);
}

/* Refined selection state (brand-colored) */
.selection-indicator {
  width: 24px;
  height: 24px;
  border-radius: 8px;
  font-size: 13px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
  opacity: 0;
  transition: opacity 0.2s ease, background 0.2s ease, border-color 0.2s ease;
}
.media-card:hover .selection-indicator,
.media-card.selected .selection-indicator { opacity: 1; }
.media-card.selected {
  border: 2px solid var(--primary-color, #2f4476);
  box-shadow: 0 0 0 4px rgba(47, 68, 118, 0.12);
}
.media-card.selected .selection-indicator {
  background: var(--primary-color, #2f4476);
  border-color: var(--primary-color, #2f4476);
  color: #fff;
}

/* List selection — match the grid's brand accent (light) */
.media-list-item.selected {
  border-color: var(--primary-color, #2f4476);
  background-color: rgba(47, 68, 118, 0.06);
  box-shadow: 0 0 0 1px rgba(47, 68, 118, 0.22);
}
.selection-checkbox.checked {
  background: var(--primary-color, #2f4476);
  border-color: var(--primary-color, #2f4476);
}

/* Staggered reveal */
.media-grid .media-card {
  animation: ml-card-in 0.4s cubic-bezier(0.2, 0.7, 0.2, 1) both;
  animation-delay: calc(var(--d, 0) * 28ms);
}
@keyframes ml-card-in {
  from { opacity: 0; transform: translateY(10px); }
}

/* Filter: icons + count pills */
.filter-icon { margin-right: 8px; color: var(--neutral-400); font-size: 15px; }
.checkbox-item:hover .filter-icon { color: var(--primary-color, #2f4476); }
.count {
  background: var(--neutral-100);
  color: var(--text-secondary);
  padding: 1px 9px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 11px;
}

/* Shimmer skeleton (replaces the empty .loader) */
.skeleton-grid { pointer-events: none; margin-bottom: 32px; }
.skeleton-card {
  border: 1px solid var(--neutral-200);
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  padding-bottom: 14px;
}
.skeleton-thumb { aspect-ratio: 1; width: 100%; }
.skeleton-thumb,
.skeleton-line {
  background: linear-gradient(90deg, #eef2f7 25%, var(--neutral-200) 50%, #eef2f7 75%);
  background-size: 200% 100%;
  animation: ml-shimmer 1.4s infinite;
}
.skeleton-line { height: 12px; border-radius: 6px; margin: 14px 12px 0; }
.skeleton-line.short { width: 50%; }
@keyframes ml-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

@media (prefers-reduced-motion: reduce) {
  .media-grid .media-card { animation: none; }
  .media-img,
  .skeleton-thumb,
  .skeleton-line { transition: none; animation: none; }
}

/* ── Dark mode — enhancement layer ── */
[data-theme="dark"] .count { background: #28364e; color: #9aadc4; }
[data-theme="dark"] .filter-icon { color: #6480a0; }
[data-theme="dark"] .checkbox-item:hover .filter-icon { color: #4e7fff; }
[data-theme="dark"] .media-card.selected {
  border-color: #4e7fff;
  box-shadow: 0 0 0 4px rgba(78, 127, 255, 0.18);
}
[data-theme="dark"] .media-card.selected .selection-indicator {
  background: #4e7fff;
  border-color: #4e7fff;
}
[data-theme="dark"] .media-card:hover .play-overlay i { background: #4e7fff; }
[data-theme="dark"] .skeleton-card { background: #28364e; border-color: #3d5070; }
[data-theme="dark"] .skeleton-thumb,
[data-theme="dark"] .skeleton-line {
  background: linear-gradient(90deg, #1e2a3e 25%, #2b3a54 50%, #1e2a3e 75%);
  background-size: 200% 100%;
}
</style>
